import { useState, useEffect, useRef, useCallback, type DragEvent } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { QuestionSet, QuestionSetItem, TeacherQuestion, BonusRoundConfig } from '@/types';
import UnsavedChangesModal from '@/components/UnsavedChangesModal';
import {
  validateQuestionSet,
  getValidationErrorMessage,
  QuestionSetValidationResult
} from '@/utils/questionValidation';

interface QuestionDraftingProps {
  questionSetId: string | null;
  onBack: () => void;
}

const BONUS_ITEMS = [
  { id: 'speed_boost', name: 'Speed Boost', icon: '💨' },
  { id: 'turbo', name: 'Turbo', icon: '⚡' },
  { id: 'shoes', name: 'Super Shoes', icon: '👟' },
  { id: 'net', name: 'Net', icon: '🕸️' },
  { id: 'shield', name: 'Shield', icon: '🛡️' },
  { id: 'gun', name: 'Gun', icon: '🔫' }
] as const;

const DEFAULT_BONUS_ITEM_IDS = BONUS_ITEMS.map(item => item.id);

const createDefaultBonusForm = () => ({
  name: '',
  selectionTimeSeconds: 8,
  availableItems: [...DEFAULT_BONUS_ITEM_IDS],
  assignRandomly: true
});

const QuestionDrafting = ({ questionSetId, onBack }: QuestionDraftingProps) => {
  const { getQuestionSet, updateQuestionSet } = useAuthStore();

  const [questionSet, setQuestionSet] = useState<QuestionSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [isEditingBonus, setIsEditingBonus] = useState(false);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  // Auto-save and navigation states
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  const initialQuestionSetRef = useRef<QuestionSet | null>(null);

  // Validation state
  const [validationResult, setValidationResult] = useState<QuestionSetValidationResult | null>(null);
  const [showValidationError, setShowValidationError] = useState(false);

  // Form states
  const [questionForm, setQuestionForm] = useState({
    text: '',
    choices: ['', ''],
    correctAnswerIndex: 0,
    questionReadingTimeSeconds: 10,
    arenaPlayTimeSeconds: 12,
    points: 'standard' as 'standard' | '2x' | '3x' | 'none',
    category: '',
    difficulty: 'mixed' as 'easy' | 'medium' | 'hard' | 'mixed',
    explanation: ''
  });

  const [bonusForm, setBonusForm] = useState(createDefaultBonusForm);

  const loadQuestionSet = useCallback(async () => {
    if (!questionSetId) return;

    try {
      const set = await getQuestionSet(questionSetId);
      setQuestionSet(set);
      initialQuestionSetRef.current = set;
    } catch (error) {
      console.error('Failed to load question set:', error);
    } finally {
      setIsLoading(false);
    }
  }, [questionSetId, getQuestionSet]);

  useEffect(() => {
    if (questionSetId) {
      loadQuestionSet();
    } else {
      setIsLoading(false);
    }
  }, [questionSetId, loadQuestionSet]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!questionSet || !questionSetId) return;

    try {
      setIsSaving(true);
      console.log('💾 Auto-saving question set...');

      // Run validation to determine if we need to demote to draft
      const validationCheck = validateQuestionSet(questionSet);

      // If validation fails and current status is 'published', demote to 'draft'
      let finalStatus = questionSet.status || 'draft';
      if (!validationCheck.isValid && questionSet.status === 'published') {
        finalStatus = 'draft';
        console.log('⚠️ Validation failed - demoting to draft status');
      }

      const updatedSet = {
        ...questionSet,
        status: finalStatus as 'draft' | 'published',
        updatedAt: new Date()
      };

      await updateQuestionSet(questionSet.id, updatedSet);

      // Update local state to reflect the new status
      if (finalStatus !== questionSet.status) {
        setQuestionSet(prev => prev ? { ...prev, status: finalStatus as 'draft' | 'published' } : null);
      }

      setLastSaved(new Date());
      setIsDirty(false);
      console.log(`✅ Auto-save successful (status: ${finalStatus})`);
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [questionSet, questionSetId, updateQuestionSet]);

  // Apply question form changes to questionSet (auto-save handles persistence)
  const applyQuestionChanges = useCallback(() => {
    if (!activeItemId) return;

    setQuestionSet(prev => {
      if (!prev) return prev;

      let questionUpdated = false;
      const updatedQuestions = prev.questions.map(item => {
        if (item.id === activeItemId && item.question) {
          questionUpdated = true;

          // Build question object without undefined values (Firestore doesn't accept them)
          const updatedQuestion: TeacherQuestion = {
            ...item.question,
            text: questionForm.text,
            choices: questionForm.choices.filter(choice => choice.trim() !== ''),
            correctAnswerIndex: questionForm.correctAnswerIndex,
            questionReadingTimeSeconds: questionForm.questionReadingTimeSeconds,
            arenaPlayTimeSeconds: questionForm.arenaPlayTimeSeconds,
            points: questionForm.points,
            difficulty: questionForm.difficulty,
            updatedAt: new Date()
          };

          // Only add optional fields if they have values (avoid undefined)
          if (questionForm.category && questionForm.category.trim()) {
            updatedQuestion.category = questionForm.category.trim();
          }
          if (questionForm.explanation && questionForm.explanation.trim()) {
            updatedQuestion.explanation = questionForm.explanation.trim();
          }

          return { ...item, question: updatedQuestion };
        }
        return item;
      });

      if (!questionUpdated) {
        return prev;
      }

      return {
        ...prev,
        questions: updatedQuestions,
        updatedAt: new Date(),
        status: prev.status || ('draft' as const)
      };
    });

    setIsDirty(true); // Mark as dirty for auto-save
  }, [activeItemId, questionForm]);

  // Apply bonus round form changes to questionSet (auto-save handles persistence)
  const applyBonusRoundChanges = useCallback(() => {
    if (!activeItemId) return;

    setQuestionSet(prev => {
      if (!prev) return prev;

      let bonusUpdated = false;
      const updatedQuestions = prev.questions.map(item => {
        if (item.id === activeItemId && item.bonusRound) {
          bonusUpdated = true;

          const updatedBonus: BonusRoundConfig = {
            ...item.bonusRound,
            name: bonusForm.name,
            selectionTimeSeconds: bonusForm.selectionTimeSeconds,
            availableItems: bonusForm.availableItems,
            assignRandomly: bonusForm.assignRandomly,
            updatedAt: new Date()
          };

          return { ...item, bonusRound: updatedBonus };
        }
        return item;
      });

      if (!bonusUpdated) {
        return prev;
      }

      return {
        ...prev,
        questions: updatedQuestions,
        updatedAt: new Date(),
        status: prev.status || ('draft' as const)
      };
    });

    setIsDirty(true); // Mark as dirty for auto-save
  }, [activeItemId, bonusForm]);

  // Debounced auto-save effect
  useEffect(() => {
    if (isDirty && questionSet) {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout for auto-save (2 seconds after last change)
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [isDirty, questionSet, autoSave]);

  // Browser beforeunload protection
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Track form changes and apply them to questionSet for auto-save
  useEffect(() => {
    if (isEditingQuestion && activeItemId) {
      applyQuestionChanges();
    }
  }, [questionForm, isEditingQuestion, activeItemId, applyQuestionChanges]);

  useEffect(() => {
    if (isEditingBonus && activeItemId) {
      applyBonusRoundChanges();
    }
  }, [bonusForm, isEditingBonus, activeItemId, applyBonusRoundChanges]);

  // Handle navigation with unsaved changes
  const handleNavigateAway = useCallback((navigateCallback: () => void) => {
    if (isDirty) {
      // Run validation before showing modal
      if (questionSet) {
        const result = validateQuestionSet(questionSet);
        setValidationResult(result);
      }
      setPendingNavigation(() => navigateCallback);
      setShowUnsavedModal(true);
    } else {
      navigateCallback();
    }
  }, [isDirty, questionSet]);

  const handleSaveAndExit = async () => {
    if (!questionSet) return;

    // Validate before publishing
    const result = validateQuestionSet(questionSet);
    setValidationResult(result);

    if (!result.isValid) {
      console.log('❌ Cannot publish: Validation failed');
      setShowValidationError(true);
      return; // Stay in modal, don't proceed
    }

    try {
      setIsSaving(true);
      const updatedSet = {
        ...questionSet,
        status: 'published' as const,
        updatedAt: new Date()
      };

      await updateQuestionSet(questionSet.id, updatedSet);
      console.log('✅ Question set published successfully');
      setShowUnsavedModal(false);
      setShowValidationError(false);
      if (pendingNavigation) {
        pendingNavigation();
      }
    } catch (error) {
      console.error('❌ Failed to save question set:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!questionSet) return;

    try {
      setIsSaving(true);
      const updatedSet = {
        ...questionSet,
        status: 'draft' as const,
        updatedAt: new Date()
      };

      await updateQuestionSet(questionSet.id, updatedSet);
      console.log('✅ Question set saved as draft');
      setShowUnsavedModal(false);
      if (pendingNavigation) {
        pendingNavigation();
      }
    } catch (error) {
      console.error('❌ Failed to save draft:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  const handleSaveQuestionSet = async () => {
    if (!questionSet) return;

    // Validate before publishing
    const result = validateQuestionSet(questionSet);
    setValidationResult(result);

    if (!result.isValid) {
      const errorMsg = getValidationErrorMessage(result);
      console.log('❌ Cannot publish: Validation failed -', errorMsg);
      setShowValidationError(true);

      // Show error notification (using browser alert for now)
      alert(`Cannot Save as Published\n\n${errorMsg}\n\nPlease complete all required fields:\n- Question text\n- First two answer choices\n\nYou can save as draft to preserve your progress.`);
      return;
    }

    try {
      setIsSaving(true);
      console.log('📦 Publishing question set...');

      const updatedSet = {
        ...questionSet,
        status: 'published' as const,
        updatedAt: new Date()
      };

      await updateQuestionSet(questionSet.id, updatedSet);
      setLastSaved(new Date());
      setIsDirty(false);
      setShowValidationError(false);
      console.log('✅ Question set published successfully');

      // Navigate back to question management
      onBack();
    } catch (error) {
      console.error('❌ Failed to publish question set:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const generateId = () => `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const addQuestion = () => {
    if (!questionSet) return;

    const newQuestion: TeacherQuestion = {
      id: generateId(),
      text: '',
      choices: ['', ''],
      correctAnswerIndex: 0,
      questionReadingTimeSeconds: 10,
      arenaPlayTimeSeconds: 12,
      points: 'standard',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const newItem: QuestionSetItem = {
      id: generateId(),
      type: 'question',
      order: 0, // Placeholder; real order assigned below
      question: newQuestion
    };

    const selectedIndex = activeItemId
      ? questionSet.questions.findIndex(item => item.id === activeItemId)
      : -1;
    const insertionIndex = selectedIndex >= 0 ? selectedIndex + 1 : questionSet.questions.length;

    const questionsWithNewItem = [...questionSet.questions];
    questionsWithNewItem.splice(insertionIndex, 0, newItem);

    const updatedQuestions = questionsWithNewItem.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    const updatedSet = {
      ...questionSet,
      questions: updatedQuestions,
      totalQuestions: updatedQuestions.filter(item => item.type === 'question').length,
      updatedAt: new Date(),
      status: questionSet.status || ('draft' as const) // Ensure status is set
    };

    setQuestionSet(updatedSet);
    setActiveItemId(newItem.id);
    setIsEditingQuestion(true);
    setIsEditingBonus(false);
    setIsDirty(true); // Mark as dirty
    setQuestionForm({
      text: newQuestion.text,
      choices: [...newQuestion.choices],
      correctAnswerIndex: newQuestion.correctAnswerIndex,
      questionReadingTimeSeconds: newQuestion.questionReadingTimeSeconds,
      arenaPlayTimeSeconds: newQuestion.arenaPlayTimeSeconds,
      points: newQuestion.points,
      category: newQuestion.category || '',
      difficulty: newQuestion.difficulty || 'mixed',
      explanation: newQuestion.explanation || ''
    });
  };

  const addBonusRound = () => {
    if (!questionSet) return;

    const newBonus: BonusRoundConfig = {
      id: generateId(),
      name: '',
      selectionTimeSeconds: 8,
      availableItems: [...DEFAULT_BONUS_ITEM_IDS],
      assignRandomly: true,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const newItem: QuestionSetItem = {
      id: generateId(),
      type: 'bonus_round',
      order: 0, // Placeholder; real order assigned below
      bonusRound: newBonus
    };

    const selectedIndex = activeItemId
      ? questionSet.questions.findIndex(item => item.id === activeItemId)
      : -1;
    const insertionIndex = selectedIndex >= 0 ? selectedIndex + 1 : questionSet.questions.length;

    const questionsWithNewItem = [...questionSet.questions];
    questionsWithNewItem.splice(insertionIndex, 0, newItem);

    const updatedQuestions = questionsWithNewItem.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    const updatedSet = {
      ...questionSet,
      questions: updatedQuestions,
      totalQuestions: updatedQuestions.filter(item => item.type === 'question').length,
      updatedAt: new Date(),
      status: questionSet.status || ('draft' as const) // Ensure status is set
    };

    setQuestionSet(updatedSet);
    setActiveItemId(newItem.id);
    setIsEditingBonus(true);
    setIsEditingQuestion(false);
    setIsDirty(true); // Mark as dirty
    setBonusForm(createDefaultBonusForm());
  };

  const deleteItem = (itemId: string) => {
    setQuestionSet(prev => {
      if (!prev) return prev;

      const updatedQuestions = prev.questions
        .filter(item => item.id !== itemId)
        .map((item, index) => ({ ...item, order: index + 1 }));

      if (updatedQuestions.length === prev.questions.length) {
        return prev;
      }

      const questionCount = updatedQuestions.filter(item => item.type === 'question').length;

      return {
        ...prev,
        questions: updatedQuestions,
        totalQuestions: questionCount,
        updatedAt: new Date(),
        status: prev.status || ('draft' as const)
      };
    });

    if (activeItemId === itemId) {
      setActiveItemId(null);
      setIsEditingQuestion(false);
      setIsEditingBonus(false);
    }

    setIsDirty(true); // Mark as dirty for auto-save
    console.log('🗑️ Item deleted, will auto-save');
  };

  const confirmAndDeleteItem = (item: QuestionSetItem) => {
    const itemLabel = item.type === 'question' ? 'question' : 'bonus round';
    const confirmationMessage = `Are you sure you want to remove this ${itemLabel}? This action cannot be undone.`;

    const shouldDelete = typeof window === 'undefined' ? true : window.confirm(confirmationMessage);
    if (!shouldDelete) {
      return;
    }

    deleteItem(item.id);
  };

  const editItem = (item: QuestionSetItem) => {
    setActiveItemId(item.id);

    if (item.type === 'question' && item.question) {
      setIsEditingQuestion(true);
      setIsEditingBonus(false);
      setQuestionForm({
        text: item.question.text,
        choices: [...item.question.choices],
        correctAnswerIndex: item.question.correctAnswerIndex,
        questionReadingTimeSeconds: item.question.questionReadingTimeSeconds,
        arenaPlayTimeSeconds: item.question.arenaPlayTimeSeconds,
        points: item.question.points,
        category: item.question.category || '',
        difficulty: item.question.difficulty || 'mixed',
        explanation: item.question.explanation || ''
      });
    } else if (item.type === 'bonus_round' && item.bonusRound) {
      setIsEditingBonus(true);
      setIsEditingQuestion(false);
      setBonusForm({
        name: item.bonusRound.name,
        selectionTimeSeconds: item.bonusRound.selectionTimeSeconds,
        availableItems: item.bonusRound.availableItems?.length
          ? [...item.bonusRound.availableItems] as typeof DEFAULT_BONUS_ITEM_IDS
          : [...DEFAULT_BONUS_ITEM_IDS],
        assignRandomly: item.bonusRound.assignRandomly ?? true
      });
    }
  };

  const handleDragStart = (e: DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: DragEvent, targetItemId: string) => {
    e.preventDefault();

    if (!draggedItemId || !questionSet || draggedItemId === targetItemId) return;

    const draggedIndex = questionSet.questions.findIndex(item => item.id === draggedItemId);
    const targetIndex = questionSet.questions.findIndex(item => item.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const reorderedQuestions = [...questionSet.questions];
    const [draggedItem] = reorderedQuestions.splice(draggedIndex, 1);
    reorderedQuestions.splice(targetIndex, 0, draggedItem);

    // Update order numbers
    const updatedQuestions = reorderedQuestions.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    const updatedSet = {
      ...questionSet,
      questions: updatedQuestions,
      updatedAt: new Date(),
      status: questionSet.status || ('draft' as const)
    };

    setQuestionSet(updatedSet);
    setDraggedItemId(null);
    setIsDirty(true); // Mark as dirty for auto-save
    console.log('🔄 Items reordered, will auto-save');
  };

  const statusIcon = isSaving ? '⏳' : isDirty ? '⏳' : lastSaved ? '✅' : '📝';
  const statusMessage = isSaving ? 'Publishing...' : isDirty ? 'Unsaved changes' : lastSaved ? 'All changes saved' : 'Ready to edit';

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(102, 126, 234, 0.3)',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <h3 style={{ color: '#374151', margin: '0 0 0.5rem' }}>
            Loading Question Set
          </h3>
          <p style={{ color: '#64748b', margin: 0 }}>
            Please wait while we load your questions...
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!questionSet) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '3rem',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
        <h2 style={{ color: '#374151', marginBottom: '1rem' }}>Question Set Not Found</h2>
        <p style={{ color: '#64748b', marginBottom: '2rem' }}>
          The question set you're trying to edit could not be found.
        </p>
        <button
          onClick={onBack}
          style={{
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ← Back to Question Sets
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Draft Overview */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '1.25rem 1.5rem',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        alignItems: 'stretch'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          background: 'rgba(102, 126, 234, 0.05)',
          borderRadius: '12px',
          padding: '0.75rem 1rem',
          minHeight: '56px',
          flex: '1 1 260px',
          minWidth: '220px'
        }}>
          <span style={{
            display: 'inline-block',
            width: '28px',
            fontSize: '1.4rem',
            animation: isSaving ? 'spin 1s linear infinite' : 'none',
            opacity: isSaving ? 0.6 : 1,
            transition: 'opacity 0.2s ease'
          }}>
            {statusIcon}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <span style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#374151'
            }}>
              {statusMessage}
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                color: '#64748b',
                maxWidth: '260px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={questionSet.name}
            >
              {questionSet.name}
            </span>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          flex: '1 1 320px'
        }}>
          <button
            onClick={handleSaveQuestionSet}
            disabled={isSaving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
              boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
              opacity: isSaving ? 0.6 : 1,
              minWidth: '160px'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(102, 126, 234, 0.3)';
              }
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>📦</span>
            <span>Save Questions</span>
          </button>

          <button
            onClick={addQuestion}
            style={{
              padding: '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              minWidth: '150px',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            + Question
          </button>

          <button
            onClick={addBonusRound}
            style={{
              padding: '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              minWidth: '150px',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            + Bonus
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2rem', minHeight: '80vh' }}>
        {/* Timeline Panel */}
        <div style={{
          flex: '0 0 400px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '1.5rem',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          maxHeight: '80vh',
          overflow: 'auto',
          boxSizing: 'border-box' // Include padding in width calculation
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid #f3f4f6'
          }}>
            <div>
              <h3 style={{
                margin: '0 0 0.25rem',
                fontSize: '1.3rem',
                color: '#374151'
              }}>
                📝 Question Timeline
              </h3>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: '#64748b'
              }}>
                {questionSet.name}
              </p>
            </div>
            <button
              onClick={() => handleNavigateAway(onBack)}
              style={{
                padding: '0.5rem',
                background: 'none',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: '1.2rem',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.color = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#64748b';
              }}
              title="Back to Question Sets"
            >
              ←
            </button>
          </div>

          {/* Timeline Items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {questionSet.questions.map((item, index) => {
              const isActive = item.id === activeItemId;
              const isQuestion = item.type === 'question';

              return (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, item.id)}
                  onClick={() => editItem(item)}
                  style={{
                    position: 'relative',
                    padding: '1rem',
                    background: isActive
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                      : isQuestion
                        ? 'rgba(16, 185, 129, 0.05)'
                        : 'rgba(245, 158, 11, 0.05)',
                    border: isActive
                      ? '2px solid #667eea'
                      : isQuestion
                        ? '2px solid rgba(16, 185, 129, 0.2)'
                        : '2px solid rgba(245, 158, 11, 0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {/* Order Number */}
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '12px',
                    background: isQuestion ? '#10b981' : '#f59e0b',
                    color: 'white',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    {index + 1}
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    draggable={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmAndDeleteItem(item);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    onDragStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      padding: '0.25rem',
                      borderRadius: '4px',
                      opacity: 0.7,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fef2f2';
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.opacity = '0.7';
                    }}
                  >
                    🗑️
                  </button>

                  {/* Content */}
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>
                        {isQuestion ? '❓' : '🎁'}
                      </span>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: '#374151'
                      }}>
                        {isQuestion ? 'Question' : 'Bonus Round'}
                      </span>
                    </div>

                    <div style={{
                      fontSize: '0.8rem',
                      color: '#64748b',
                      lineHeight: '1.3'
                    }}>
                      {isQuestion && item.question ? (
                        item.question.text || 'New question - click to edit'
                      ) : item.bonusRound ? (
                        item.bonusRound.name || 'New bonus round - click to edit'
                      ) : 'Click to edit'}
                    </div>

                    {/* Timing info */}
                    <div style={{
                      marginTop: '0.5rem',
                      fontSize: '0.7rem',
                      color: '#9ca3af'
                    }}>
                      {isQuestion && item.question ? (
                        `Read: ${item.question.questionReadingTimeSeconds}s • Play: ${item.question.arenaPlayTimeSeconds}s`
                      ) : item.bonusRound ? (
                        `Selection: ${item.bonusRound.selectionTimeSeconds}s`
                      ) : ''}
                    </div>
                  </div>
                </div>
              );
            })}

            {questionSet.questions.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
                <p>No questions yet. Add your first question to get started!</p>
              </div>
            )}
          </div>
        </div>


        {/* Editor Panel */}
        <div style={{ flex: 1 }}>
          {isEditingQuestion ? (
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '2rem',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                margin: '0 0 1.5rem',
                fontSize: '1.5rem',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                ❓ Edit Question
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Question Text */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Question Text *
                  </label>
                  <textarea
                    value={questionForm.text}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, text: e.target.value }))}
                    placeholder="Enter your question here..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: `2px solid ${!questionForm.text.trim() && showValidationError ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: '12px',
                      fontSize: '1rem',
                      resize: 'vertical',
                      transition: 'border-color 0.2s ease',
                      backgroundColor: !questionForm.text.trim() && showValidationError ? 'rgba(239, 68, 68, 0.05)' : 'white'
                    }}
                    onFocus={(e) => {
                      if (!(!questionForm.text.trim() && showValidationError)) {
                        e.target.style.borderColor = '#667eea';
                      }
                    }}
                    onBlur={(e) => {
                      if (!(!questionForm.text.trim() && showValidationError)) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  />
                  {!questionForm.text.trim() && showValidationError && (
                    <p style={{
                      margin: '0.5rem 0 0',
                      fontSize: '0.8rem',
                      color: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      ⚠️ Question text is required
                    </p>
                  )}
                </div>

                {/* Answer Choices */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Answer Choices
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {questionForm.choices.map((choice, index) => {
                      const isCompulsory = index < 2;
                      const hasError = isCompulsory && !choice.trim() && showValidationError;

                      return (
                      <div key={index}>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                          <input
                            type="radio"
                            name="correct-answer"
                            checked={questionForm.correctAnswerIndex === index}
                            onChange={() => setQuestionForm(prev => ({ ...prev, correctAnswerIndex: index }))}
                            style={{ flexShrink: 0 }}
                          />
                          <input
                            type="text"
                            value={choice}
                            onChange={(e) => {
                              const newChoices = [...questionForm.choices];
                              newChoices[index] = e.target.value;
                              setQuestionForm(prev => ({ ...prev, choices: newChoices }));
                            }}
                            placeholder={`Answer choice ${index + 1}${isCompulsory ? ' *' : ''}`}
                            style={{
                              flex: 1,
                              padding: '0.75rem',
                              border: `2px solid ${hasError ? '#ef4444' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              fontSize: '1rem',
                              transition: 'border-color 0.2s ease',
                              backgroundColor: hasError ? 'rgba(239, 68, 68, 0.05)' : 'white'
                            }}
                            onFocus={(e) => {
                              if (!hasError) {
                                e.target.style.borderColor = '#667eea';
                              }
                            }}
                            onBlur={(e) => {
                              if (!hasError) {
                                e.target.style.borderColor = '#e5e7eb';
                              }
                            }}
                          />
                        {index >= 2 && (
                          <button
                            onClick={() => {
                              const newChoices = questionForm.choices.filter((_, i) => i !== index);
                              setQuestionForm(prev => ({
                                ...prev,
                                choices: newChoices,
                                correctAnswerIndex: prev.correctAnswerIndex >= index
                                  ? Math.max(0, prev.correctAnswerIndex - 1)
                                  : prev.correctAnswerIndex
                              }));
                            }}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                        {hasError && (
                          <p style={{
                            margin: '0.5rem 0 0 2rem',
                            fontSize: '0.8rem',
                            color: '#ef4444',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            ⚠️ Answer choice {index + 1} is required
                          </p>
                        )}
                      </div>
                    );
                    })}

                    {questionForm.choices.length < 6 && (
                      <button
                        onClick={() => setQuestionForm(prev => ({
                          ...prev,
                          choices: [...prev.choices, '']
                        }))}
                        style={{
                          padding: '0.75rem',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: '2px dashed #d1d5db',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#e5e7eb';
                          e.currentTarget.style.borderColor = '#9ca3af';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f3f4f6';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                      >
                        + Add Another Choice
                      </button>
                    )}
                  </div>
                </div>

                {/* Question Settings */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '1rem'
                }}>
                  {/* Reading Time */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Reading Time (s)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="60"
                      value={questionForm.questionReadingTimeSeconds}
                      onChange={(e) => setQuestionForm(prev => ({
                        ...prev,
                        questionReadingTimeSeconds: parseInt(e.target.value) || 10
                      }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Arena Time */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Arena Time (s)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="60"
                      value={questionForm.arenaPlayTimeSeconds}
                      onChange={(e) => setQuestionForm(prev => ({
                        ...prev,
                        arenaPlayTimeSeconds: parseInt(e.target.value) || 12
                      }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Points */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Points
                    </label>
                    <select
                      value={questionForm.points}
                      onChange={(e) => setQuestionForm(prev => ({
                        ...prev,
                        points: e.target.value as 'standard' | '2x' | '3x' | 'none'
                      }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        background: 'white'
                      }}
                    >
                      <option value="standard">Standard (100)</option>
                      <option value="2x">Double (200)</option>
                      <option value="3x">Triple (300)</option>
                      <option value="none">No Points</option>
                    </select>
                  </div>
                </div>

                {/* Additional Settings */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Category (Optional)
                    </label>
                    <input
                      type="text"
                      value={questionForm.category}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="e.g., Mathematics, History"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Difficulty
                    </label>
                    <select
                      value={questionForm.difficulty}
                      onChange={(e) => setQuestionForm(prev => ({
                        ...prev,
                        difficulty: e.target.value as 'easy' | 'medium' | 'hard' | 'mixed'
                      }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        background: 'white'
                      }}
                    >
                      <option value="easy">🟢 Easy</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="hard">🔴 Hard</option>
                      <option value="mixed">🌈 Mixed</option>
                    </select>
                  </div>
                </div>

                {/* Explanation */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Explanation (Optional)
                  </label>
                  <textarea
                    value={questionForm.explanation}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, explanation: e.target.value }))}
                    placeholder="Explain the correct answer or provide additional context..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Close Editor Button */}
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => {
                      setIsEditingQuestion(false);
                      setActiveItemId(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    ✓ Done Editing
                  </button>
                </div>
              </div>
            </div>
          ) : isEditingBonus ? (
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '2rem',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                margin: '0 0 1.5rem',
                fontSize: '1.5rem',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🎁 Edit Bonus Round
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Bonus Round Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Bonus Round Name
                  </label>
                  <input
                    type="text"
                    value={bonusForm.name}
                    onChange={(e) => setBonusForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Speed Boost Round"
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Selection Time */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Selection Time (seconds)
                  </label>
                  <input
                    type="number"
                    min="3"
                    max="30"
                    value={bonusForm.selectionTimeSeconds}
                    onChange={(e) => setBonusForm(prev => ({
                      ...prev,
                      selectionTimeSeconds: parseInt(e.target.value) || 8
                    }))}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* Random Assignment */}
                <div style={{
                  padding: '1rem',
                  border: '2px solid #f3f4f6',
                  borderRadius: '12px',
                  background: bonusForm.assignRandomly ? 'rgba(102, 126, 234, 0.08)' : '#ffffff',
                  transition: 'background 0.2s ease, border-color 0.2s ease'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={bonusForm.assignRandomly}
                      onChange={(e) => setBonusForm(prev => ({
                        ...prev,
                        assignRandomly: e.target.checked
                      }))}
                    />
                    Enable random card reveal
                  </label>
                  <p style={{
                    margin: '0.5rem 0 0',
                    fontSize: '0.8rem',
                    color: '#64748b',
                    lineHeight: 1.4
                  }}>
                    Automatically assign a random bonus item to each player with a dramatic reveal animation.
                  </p>
                </div>

                {/* Available Items */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Available Items
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '0.75rem'
                  }}>
                    {BONUS_ITEMS.map((item) => (
                      <label
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#667eea';
                          e.currentTarget.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={bonusForm.availableItems.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBonusForm(prev => ({
                                ...prev,
                                availableItems: [...prev.availableItems, item.id]
                              }));
                            } else {
                              setBonusForm(prev => ({
                                ...prev,
                                availableItems: prev.availableItems.filter(id => id !== item.id)
                              }));
                            }
                          }}
                        />
                        <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{item.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Close Editor Button */}
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => {
                      setIsEditingBonus(false);
                      setActiveItemId(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    ✓ Done Editing
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '3rem',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '400px'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✏️</div>
              <h3 style={{ color: '#374151', marginBottom: '1rem' }}>
                Select an Item to Edit
              </h3>
              <p style={{ color: '#64748b', marginBottom: '2rem' }}>
                Click on a question or bonus round from the timeline to start editing, or add a new item to get started.
              </p>
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center'
              }}>
                <button
                  onClick={addQuestion}
                  style={{
                    padding: '1rem 2rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  ➕ Add Question
                </button>
                <button
                  onClick={addBonusRound}
                  style={{
                    padding: '1rem 2rem',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  🎁 Add Bonus Round
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onSaveAndExit={handleSaveAndExit}
        onSaveAsDraft={handleSaveAsDraft}
        onDiscard={handleDiscard}
        onCancel={() => {
          setShowUnsavedModal(false);
          setPendingNavigation(null);
          setShowValidationError(false);
        }}
        hasValidationErrors={validationResult ? !validationResult.isValid : false}
        validationErrorMessage={validationResult ? getValidationErrorMessage(validationResult) : ''}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default QuestionDrafting;
